import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import { browserHistory } from 'react-router';

import { localize } from 'react-localize-redux';
import { connect } from 'react-redux';

import Auth from 'j-toker';
import Lottie from 'react-lottie';
import successAnimationData from 'assets/success.json';

import FlatButton from 'material-ui/FlatButton';
import TextField from '@material-ui/core/TextField';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';

import { getTranslatedString } from 'helpers/i18n';
import { AccentColor } from 'helpers/AccentColorHelper';
import { colors } from 'helpers/ColorHelper';
import {
  passwordValidation,
  passwordValidationWeak,
  passwordConfirmationValidation,
} from 'helpers/PasswordHelper';
import PasswordRequirements from '../PasswordRequirements';
import LanguageOptionsCentered from 'components/shared/LanguageOptionsCentered';

const StyledButton = styled(FlatButton)`
  margin-top: 12px !important;
  margin-bottom: 12px !important;
  width: 100%;
  color: white !important;
  fontsize: 16px;
  height: 50px !important;
  background-color: ${props => (props.disabled ? colors.DISABLED_GRAY : AccentColor())} !important;
`;

const StyledCard = styled(Card)`
  color: #424242 !important;
  font-family: 'Circular', sans-serif !important;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
`;

const defaultAnimationOptions = {
  loop: false,
  autoplay: true,
  animationData: successAnimationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

let Login = connect(store => {
  return {
    userType: store.userTypes.loginAuth,
    landlordId: store.userTypes.landlordId,
    accountProviderIsSSO: store.userTypes.accountProviderIsSSO,
  };
})(
  class PasswordReset extends Component {
    constructor(props) {
      super(props);
      this.props = props;
      this.state = {
        password: '',
        password_confirmation: '',
        isButtonEnabled: false,
        hasUpdatedPassword: false,
        errorMessage: '',
      };
    }

    handleInputChange = event => {
      this.setState(
        {
          [event.target.name]: event.target.value.trim(),
          errorMessage: '',
        },
        () => {
          if (this.handlePasswordValidation()) {
            this.setState({ isButtonEnabled: true });
          }
        }
      );
    };

    handlePasswordValidation = () => {
      const { password, password_confirmation } = this.state;
      let validPassword = false;

      // maintenance users prefer to keep the password simple
      if (this.props.userType === 'maintenance') {
        validPassword = passwordValidationWeak(password);
      } else {
        validPassword = passwordValidation(password);
      }
      return validPassword && passwordConfirmationValidation(password, password_confirmation);
    };

    handleUpdatePassword = () => {
      const { password, password_confirmation } = this.state;
      this.setState({ isButtonEnabled: false });

      const auth = Auth;
      const query = browserHistory.getCurrentLocation().query;

      Auth.updatePassword({
        password: password,
        password_confirmation: password_confirmation,
        config: this.props.userType,
        reset_password_token: query.reset_password_token,
      })
        .then(_response => {
          auth.configDfd = null;
          this.setState({ hasUpdatedPassword: true, errorMessage: '' });
          this.props.showSuccess(getTranslatedString('success.passwordUpdated'));
        })
        .catch(error => {
          auth.configDfd = null;
          this.setState({ isButtonEnabled: true });
          if (error.data.errors) {
            if (error.data.errors[0] === 'Unauthorized') {
              this.setState({ errorMessage: getTranslatedString('passwordTokenExpired') });
            } else if (error.data.errors.full_messages[0] === 'Password was used previously.') {
              this.setState({ errorMessage: getTranslatedString('passwordUsedPreviously') });
            }
          } else {
            this.setState({ errorMessage: getTranslatedString('unknownError') });
          }
        });
    };

    getUrlForUserType = userType => {
      if (userType === 'landlord') return '/companies';
      if (userType === 'maintenance') return '/maintenance';
      return '/';
    };

    render() {
      if (this.props.accountProviderIsSSO === true && this.props.userType === 'landlord') {
        browserHistory.push('/companies');
        return null;
      }

      const loginUrl = this.getUrlForUserType(this.props.userType);

      return (
        <div style={{ marginTop: '20px', maxWidth: '320px', minWidth: '300px' }}>
          <StyledCard>
            <CardContent style={{ padding: '0 0 10px' }}>
              {this.state.hasUpdatedPassword ? (
                <PasswordSuccessfullyUpdated buttonCallback={() => browserHistory.push(loginUrl)} />
              ) : (
                <PasswordInputFields
                  password={this.state.password}
                  password_confirmation={this.state.password_confirmation}
                  errorMessage={this.state.errorMessage}
                  handleInputChange={this.handleInputChange}
                  handleUpdatePassword={this.handleUpdatePassword}
                  isButtonEnabled={this.state.isButtonEnabled}
                  backToLoginUrl={loginUrl}
                  isMaintenanceUser={this.props.userType === 'maintenance'}
                />
              )}
              <LanguageOptionsCentered />
            </CardContent>
          </StyledCard>
        </div>
      );
    }
  }
);

export default localize()(Login);

const PasswordSuccessfullyUpdated = ({ buttonCallback }) => (
  <Fragment>
    <Lottie options={defaultAnimationOptions} height={150} width={150} />
    <div>
      <h3 style={{ padding: '0 0 10px' }}>{getTranslatedString('success.passwordUpdated')}</h3>
    </div>
    <div className="row" style={{ textAlign: 'left' }}>
      <StyledButton
        name="submit"
        label={getTranslatedString('passwordReset.backToLogin')}
        primary={true}
        onClick={buttonCallback}
        labelStyle={{ textTransform: 'capitalize', fontSize: '18px' }}
      />
    </div>
  </Fragment>
);

const PasswordInputFields = ({
  password,
  password_confirmation,
  handleInputChange,
  errorMessage,
  handleUpdatePassword,
  isButtonEnabled,
  backToLoginUrl,
  isMaintenanceUser = false,
}) => (
  <Fragment>
    <div>
      <h3>{getTranslatedString('createNewPassword')}</h3>
    </div>
    {errorMessage && (
      <div>
        <p
          style={{
            color: 'red',
            marginTop: '0',
            marginBottom: '14px',
            fontWeight: '600',
            fontSize: '16px',
            width: '80%',
            display: 'inline-block',
          }}
        >
          {errorMessage}
        </p>
      </div>
    )}
    <div className="row" style={{ textAlign: 'left' }}>
      <TextField
        name="password"
        label={getTranslatedString('enterPassword')}
        inputProps={{ 'aria-label': getTranslatedString('enterPassword') }}
        onChange={handleInputChange}
        value={password}
        type="password"
        variant="outlined"
        style={{ marginTop: '5px' }}
      />
      <TextField
        name="password_confirmation"
        label={getTranslatedString('repeatPassword')}
        inputProps={{ 'aria-label': getTranslatedString('repeatPassword') }}
        onChange={handleInputChange}
        value={password_confirmation}
        type="password"
        variant="outlined"
        style={{ marginTop: '15px' }}
      />
    </div>
    {/* Only show secure password requirements for lead and agent */}
    {!isMaintenanceUser && PasswordRequirements(password, password_confirmation)}
    <StyledButton
      name="submit"
      label={getTranslatedString('passwordReset.updatePassword')}
      primary={true}
      onClick={handleUpdatePassword}
      hoverColor={colors.DISABLED_GRAY}
      disabled={!isButtonEnabled}
      labelStyle={{ textTransform: 'capitalize', fontSize: '18px' }}
    />
    <div id="account-links-container">
      <p className="account-link">
        <a href={backToLoginUrl}>{getTranslatedString('passwordReset.backToLogin')}</a>
      </p>
    </div>
  </Fragment>
);
